import React, { useContext, useState, useCallback } from 'react';
import { CouncilContext } from '../../../../contexts/CouncilContext';
import { useExpeditionLog } from '../../../../stores/expeditionLog';
import { CockpitBand } from './CockpitBand';
import { CockpitRolldown, type CockpitTab } from './CockpitRolldown';
import { HistoryTab } from './tabs/HistoryTab';
import { councilApiClient } from '../../../../services/councilApiClient';

const LS_OPEN = 'cockpit.rolldownOpen';
const LS_TAB = 'cockpit.activeTab';

export const Cockpit: React.FC<{ sessionId: string; darkMode: boolean }> = ({ sessionId, darkMode }) => {
  const councilCtx = useContext(CouncilContext);
  const { logState } = useExpeditionLog();
  const [open, setOpen] = useState<boolean>(() => localStorage.getItem(LS_OPEN) === '1');
  const [tab, setTab] = useState<CockpitTab>(() => (localStorage.getItem(LS_TAB) as CockpitTab) || 'peers');

  const toggle = useCallback(() => setOpen(o => { const n = !o; localStorage.setItem(LS_OPEN, n ? '1' : '0'); return n; }), []);
  const onTab = useCallback((t: CockpitTab) => { setTab(t); localStorage.setItem(LS_TAB, t); }, []);

  // S3 posture write — Cockpit owns the picker open-state + pending/error so they can
  // render through the round-trip; close ONLY on POST success. (hooks ABOVE the guard.)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [posturePending, setPending] = useState(false);
  const [postureError, setPostureError] = useState<string | null>(null);
  const onSetPosture = useCallback(async (p: 'advisory' | 'gated' | 'autonomous') => {
    setPending(true); setPostureError(null);
    try {
      await councilApiClient.setGovernorPolicy(sessionId, p);
      await councilCtx?.refresh();   // refresh swallows its own errors (best-effort); WS backstop reconciles
      setPickerOpen(false);          // close ONLY on POST success
    } catch (e) {
      setPostureError(e instanceof Error ? e.message : 'failed');   // keep open + show error
    } finally { setPending(false); }
  }, [sessionId, councilCtx?.refresh]);

  if (!councilCtx?.state) return null;        // non-council surface — no cockpit
  const s = councilCtx.state as any;
  const posture = s.metadata?.autonomy_rules?.governor_policy?.throttle ?? 'advisory';

  return (
    <div className="flex flex-col" style={{ width: 'min(680px, calc(100% - 16px))' }}>
      <CockpitBand sessionId={sessionId} cost={s.cost} roster={s.roster ?? []} metadata={s.metadata} entries={logState.entries}
        unread={logState.unread} posture={posture} rolldownOpen={open} onToggleRolldown={toggle} darkMode={darkMode}
        onSetPosture={onSetPosture} posturePending={posturePending} postureError={postureError}
        pickerOpen={pickerOpen} setPickerOpen={setPickerOpen} />
      {open && (
        <CockpitRolldown activeTab={tab} onTab={onTab} cost={s.cost} roster={s.roster ?? []}
          personasById={s.personasById ?? {}} entries={logState.entries} sessionId={sessionId} darkMode={darkMode}
          historySlot={<HistoryTab sessionId={sessionId} darkMode={darkMode} />} />
      )}
    </div>
  );
};
